import { z } from 'zod';

import {
    animationFeature,
    backgroundFeature,
    backgroundFileSchema,
    borderFeature,
    borderRadiusFeature,
    borderSideV0Schema,
    scrollEffectFeature,
    shadowFeature,
} from '../../features';
import { shadowSettingsSchemaLegacy } from '../../widget/common/schemas/legacy';

/**
 * How much of the row the column takes, and in which unit — `col` for twelfths
 * of the grid, `%` for a share of the row. The row picks the mode
 * (`isFlexibleWidths` on the section) and every writer either preserves the unit
 * or converts value and unit together, so the number can never be read in a unit
 * it was not written in. Legacy kept the two units as two fields that drifted
 * apart the moment one of them was resized.
 */
export const columnWidthSchema = z.object({
    value: z.number(),
    unit: z.enum(['col', '%']),
});

/** The picked background file — the feature's, shared with the block. */
export const columnBackgroundImageSchema = backgroundFileSchema;

/**
 * One contour side. Legacy stored a `borderWidth/Style/Color` shorthand plus a
 * full set of per-side keys and let the side fall back to the shorthand; v0
 * keeps only the sides, with the fallback already resolved by the migration.
 */
/** Self-versioned, shared with the block — see `features/border`. */
export const columnBorderSideSchema = borderSideV0Schema;
export const columnBorderSchema = borderFeature.fieldSchema;

/**
 * Corners, same story as the contour: legacy's `borderRadius` shorthand is
 * resolved into the four corners at migration time and only the flag survives.
 */
/** Self-versioned — see `features/border-radius`. */
export const columnRadiusSchema = borderRadiusFeature.fieldSchema;

/**
 * Everything the column paints behind its widgets. Self-versioned and shared
 * with the block — see `features/background`; a column never writes the
 * feature's `video` or `scrollEffect` (its scroll effect is its own feature).
 */
export const columnBackgroundSchema = backgroundFeature.fieldSchema;

/** Self-versioned — see `features/shadow`. */
export const columnShadowSchema = shadowFeature.fieldSchema;

/** Self-versioned — see `features/scroll-effect`. */
export const columnScrollEffectSchema = scrollEffectFeature.fieldSchema;

/** Self-versioned, shared with the widgets — see `features/animation`. */
export const columnAnimationSchema = animationFeature.fieldSchema;

/**
 * Alignment vocabularies, spelled out the way the section's `verticalPosition`
 * was. Nullable because header columns are seeded without them: those two are
 * laid out by the row, not by the column.
 *
 * Flat, unlike the rest of the column's styling. Legacy's per-device override
 * bag (`columnDeviceDataSchemaLegacy`) carries the background, the contour and
 * the two overlays — but never an alignment; the panel offers one control for
 * all three tabs, and the CSS emits `--column-justify` without device twins. The
 * box around a column is per-breakpoint; how its widgets line up inside it is
 * not.
 */
export const columnVerticalAlignSchema = z.enum(['top', 'middle', 'bottom']).nullable();
export const columnHorizontalAlignSchema = z.enum(['left', 'center', 'right']).nullable();
